import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './icons.jsx'
import {
  createWhatsappInstance,
  createWhatsappConnectLink,
  resolveWhatsappInstanceName,
  getWhatsappInstanceState,
  resetWhatsappInstance,
} from '../db/api.js'
import { friendlyWaError, WA_TEMP_BLOCK_HINT } from '../lib/waErrors.js'

/** A partir de quantas tentativas (conectar/reiniciar) na mesma sessão do
 * modal mostramos a dica de bloqueio temporário do WhatsApp (issue #82). */
const BLOCK_HINT_ATTEMPTS = 3

const CLOSE_MS = 200
const POLL_MS = 15_000

/**
 * Modal de integração WhatsApp (Evolution) por cliente.
 *
 * Fluxo (sem QR dentro do sistema — QR só na página pública):
 *  - "Conectar WhatsApp" chama o ensure idempotente no backend:
 *      • já conectado      → mensagem amigável, sem link;
 *      • existe/desconectado → mensagem de reconexão + link;
 *      • não existe          → cria e gera link.
 *  - Após gerar o link, faz polling do estado: ao conectar, mostra sucesso
 *    automaticamente (sem refresh). O Perfil também atualiza via seu polling.
 *
 * onClose pode ser usado pelo pai para recarregar os status dos clientes.
 */
export default function WhatsappIntegrationModal({ open, client, onClose }) {
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [link, setLink] = useState(null)
  const [connected, setConnected] = useState(false)
  const [reconnect, setReconnect] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [showBlockHint, setShowBlockHint] = useState(false)
  const timerRef = useRef(null)
  const attemptsRef = useRef(0)

  const clientId = client?.id
  // Nome real da instância do client (resolvido pelo client_id, não pelo padrão).
  const [instanceName, setInstanceName] = useState(null)
  useEffect(() => {
    let alive = true
    setInstanceName(null)
    if (open && clientId) {
      resolveWhatsappInstanceName(clientId).then((n) => { if (alive) setInstanceName(n) })
    }
    return () => { alive = false }
  }, [open, clientId])

  const startClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      setMounted(false)
      onClose?.()
    }, CLOSE_MS)
  }, [onClose])

  // Sincroniza montagem e reseta estado a cada abertura.
  useEffect(() => {
    if (open) {
      setMounted(true)
      setClosing(false)
      setLink(null)
      setConnected(false)
      setReconnect(false)
      setError('')
      setCopied(false)
      setShowBlockHint(false)
      attemptsRef.current = 0
    }
  }, [open])

  // Esc fecha.
  useEffect(() => {
    if (!mounted) return
    const onKey = (e) => e.key === 'Escape' && startClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mounted, startClose])

  // Polling: depois que o link é gerado, detecta a conexão e mostra sucesso.
  useEffect(() => {
    if (!mounted || !link || connected || !instanceName) return
    const check = async () => {
      try {
        const r = await getWhatsappInstanceState(instanceName)
        if (r.state === 'open') {
          setConnected(true)
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
        }
      } catch {
        /* silencioso — segue tentando */
      }
    }
    timerRef.current = setInterval(check, POLL_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [mounted, link, connected, instanceName])

  async function handleConnect() {
    if (!clientId) {
      setError('Cliente inválido.')
      return
    }
    setError('')
    setCopied(false)
    setLoading(true)
    attemptsRef.current += 1
    if (attemptsRef.current >= BLOCK_HINT_ATTEMPTS) setShowBlockHint(true)
    try {
      // Ensure idempotente: nunca recria uma instância existente.
      const name = instanceName || (await resolveWhatsappInstanceName(clientId))
      const inst = await createWhatsappInstance({ instanceName: name, clientId })
      setInstanceName(inst.instance_name || name)
      if (inst.status === 'open') {
        setConnected(true)
        return
      }
      setReconnect(!!inst.already_existed)
      const r = await createWhatsappConnectLink(inst.instance_name || name)
      setLink(r.url)
    } catch (e) {
      setError(friendlyWaError(e.message))
    } finally {
      setLoading(false)
    }
  }

  /** Só desloga (instância e histórico continuam) e já gera um QR novo em cima da mesma sessão. */
  async function handleReset() {
    if (!instanceName) return
    setError('')
    setResetting(true)
    try {
      await resetWhatsappInstance(instanceName)
      setLink(null)
      setReconnect(false)
      await handleConnect()
    } catch (e) {
      setError(friendlyWaError(e.message))
    } finally {
      setResetting(false)
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard pode falhar sem HTTPS; o usuário copia manualmente */
    }
  }

  if (!mounted) return null

  return (
    <div
      className={`sup-overlay${closing ? ' is-closing' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && startClose()}
    >
      <div className="sup-modal" role="dialog" aria-modal="true">
        <button className="sup-close" onClick={startClose} aria-label="Fechar">
          <Icon n="x" s={16} />
        </button>

        <div className="sup-head">
          <div className="sup-title">Integrar WhatsApp</div>
          <div className="sup-subtitle">
            {client?.name ? `Cliente: ${client.name}` : 'Conecte o WhatsApp deste cliente.'}
          </div>
        </div>

        {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

        {showBlockHint && !connected && (
          <div
            style={{
              marginBottom: 12, padding: '10px 12px', borderRadius: 10,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309',
              fontSize: 12.5, lineHeight: 1.5,
            }}
          >
            {WA_TEMP_BLOCK_HINT}
          </div>
        )}

        {connected ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div
              style={{
                width: 56, height: 56, borderRadius: '50%', margin: '0 auto 12px',
                background: '#f0fdf4', color: '#16a34a', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700,
              }}
            >
              ✓
            </div>
            <p style={{ margin: 0, fontWeight: 700, color: '#16a34a' }}>
              Seu WhatsApp já está conectado ao TITAM AI.
            </p>
          </div>
        ) : !link ? (
          <>
            <p className="sup-subtitle" style={{ marginBottom: 16 }}>
              Gere um link único de conexão. Você o enviará/abrirá em uma nova aba,
              onde o QR Code aparecerá para escanear no WhatsApp.
            </p>
            <button className="btn btn-black btn-full" onClick={handleConnect} disabled={loading}>
              {loading ? 'Verificando…' : 'Conectar WhatsApp'}
            </button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="sup-subtitle">
              {reconnect
                ? 'Reconecte seu WhatsApp escaneando o QR Code. Abra o link abaixo em uma nova aba:'
                : 'Link de conexão gerado. Abra em uma nova aba para exibir o QR Code (expira após o uso):'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" value={link} readOnly onFocus={(e) => e.target.select()} />
              <button className="btn btn-black btn-sm" onClick={handleCopy}>
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
            <a className="btn btn-outline btn-full" href={link} target="_blank" rel="noopener noreferrer">
              Abrir em nova aba
            </a>
            <p className="sup-subtitle" style={{ fontSize: 12, textAlign: 'center' }}>
              Esta janela atualiza sozinha quando o WhatsApp conectar.
            </p>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleReset}
              disabled={resetting}
              style={{ alignSelf: 'center' }}
            >
              {resetting ? 'Reiniciando…' : 'QR não funciona? Reiniciar instância'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
